import { v7 as uuidV7 } from 'uuid';

import { GateTypes, Requirement, RequirementMap } from '@/types/registration.types';

function getEmptyGateRequirement(type = GateTypes.domain_email): Requirement {
  return { type, id: uuidV7() };
}

const getBaseGateRequirement = ({
  existingRequirement,
  gateType,
}: {
  existingRequirement: Requirement | undefined;
  gateType: GateTypes | null;
}): Requirement | null => {
  switch (gateType) {
    case GateTypes.domain_email:
      return existingRequirement || getEmptyGateRequirement(GateTypes.domain_email);
    case GateTypes.shared_passcode:
      return existingRequirement || getEmptyGateRequirement(GateTypes.shared_passcode);
    default:
      /**
       * Returns null for:
       *  - GateTypes.passcode_list: the Audiences page owns this slot. It writes/reads
       *    `requirements[passcode_list]` as `Requirement[]` directly when the user attaches
       *    or detaches lists; the gating panel must not seed or overwrite it here.
       *    See: src/micros/edit-project/grid-areas/main/audiences/components/listsv2/select-list-modal.tsx
       *  - GateTypes.password: password gating uses `password_gating_enabled` and has no
       *    entry in `requirements`.
       */
      return null;
  }
};

const handleAccessTypeRequirementsChange = ({
  requirementsMap,
  projectGateType,
}: {
  requirementsMap: RequirementMap;
  projectGateType: GateTypes | null;
}): RequirementMap => {
  // should return either the existing type data with updated values, or create a default data structure for the gate type if it doesn't exist yet.
  const baseRequirement = getBaseGateRequirement({
    existingRequirement:
      Object.values(requirementsMap).find((requirement) => requirement.type === projectGateType) || undefined,
    gateType: projectGateType || null, // default to domain gate type for empty state, but it doesn't actually matter which type we use here since the function will return null for password gate type, which is the only type that should be returning null.
  });

  return {
    ...requirementsMap,
    ...(baseRequirement && projectGateType
      ? {
          [projectGateType]: baseRequirement,
        }
      : {}),
  };
};

export default handleAccessTypeRequirementsChange;
